/*
  <otw-video-reels> — homepage video reels (PRD 8.1 row 16).
  - Card videos are muted/loop/preload=none. Their src is set when the card is near the viewport (IntersectionObserver,
    rootMargin 200px) and they play only while >= 60% visible.
  - Tapping a card opens the fullscreen <otw-drawer> player: unmuted, tap to pause, swipe up/down or arrows to change
    reel, progress bar, "Shop this" mini-card with an add button, close.
  The drawer is moved to <body> so a transformed ancestor (scroll reveal) can't break position: fixed.
*/
(() => {
  if (customElements.get('otw-video-reels')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  class OtwVideoReels extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-reel]'));
      this.drawer = document.getElementById(this.dataset.drawer);
      this.abort = new AbortController();
      this.index = 0;
      if (!this.cards.length) return;

      this.observeCards();
      if (this.drawer) {
        this.parent = this.drawer.parentNode;
        document.body.appendChild(this.drawer);
        this.setupPlayer();
      }
      this.addEventListener('click', (event) => {
        const card = event.target.closest('[data-reel]');
        if (card) this.open(this.cards.indexOf(card), card);
      }, { signal: this.abort.signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.io?.disconnect();
      this.near?.disconnect();
      if (this.drawer) {
        if (this.drawer.isOpen) this.drawer.close({ restoreFocus: false });
        this.drawer.remove();
      }
    }

    /* ---------- Cards ---------- */
    observeCards() {
      if (!('IntersectionObserver' in window)) return;
      this.near = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const video = entry.target.querySelector('video');
            if (video && !video.getAttribute('src') && entry.target.dataset.src) {
              video.src = entry.target.dataset.src;
              video.load();
              // The play observer may already have reported this card as visible before it had a src
              if (entry.target.otwVisible) this.playCard(entry.target);
            }
            this.near.unobserve(entry.target);
          });
        },
        { rootMargin: '200px' }
      );
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            entry.target.otwVisible = entry.isIntersecting && entry.intersectionRatio >= 0.6;
            if (!video || !video.getAttribute('src')) return;
            if (entry.target.otwVisible && !this.playerOpen && !reduced.matches) {
              this.playCard(entry.target);
            } else {
              video.pause();
              entry.target.classList.remove('is-playing');
            }
          });
        },
        { threshold: [0, 0.6, 1] }
      );
      this.cards.forEach((card) => {
        this.near.observe(card);
        this.io.observe(card);
      });
    }

    playCard(card) {
      const video = card.querySelector('video');
      if (!video || this.playerOpen || reduced.matches) return;
      video
        .play()
        .then(() => {
          // A scroll-away while the video was loading must win
          if (card.otwVisible) card.classList.add('is-playing');
          else video.pause();
        })
        .catch(() => {});
    }

    pauseCards() {
      this.cards.forEach((card) => {
        card.querySelector('video')?.pause();
        card.classList.remove('is-playing');
      });
    }

    /* ---------- Player ---------- */
    setupPlayer() {
      const d = this.drawer;
      const signal = this.abort.signal;
      this.player = d.querySelector('[data-player]');
      this.bar = d.querySelector('[data-progress]');
      this.captionEl = d.querySelector('[data-caption]');
      this.productEl = d.querySelector('[data-product]');
      this.pausedIcon = d.querySelector('[data-paused]');
      this.stage = d.querySelector('[data-stage]');

      d.querySelector('[data-tap]').addEventListener('click', () => this.toggle(), { signal });
      d.querySelector('[data-prev]').addEventListener('click', () => this.go(-1), { signal });
      d.querySelector('[data-next]').addEventListener('click', () => this.go(1), { signal });
      d.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') { event.preventDefault(); this.go(1); }
        else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') { event.preventDefault(); this.go(-1); }
        else if (event.key === ' ' && event.target === d.panel) { event.preventDefault(); this.toggle(); }
      }, { signal });
      d.addEventListener('otw:drawer:close', () => this.onClose(), { signal });

      this.player.addEventListener('timeupdate', () => {
        const { currentTime, duration } = this.player;
        if (duration > 0) this.bar.style.transform = `scaleX(${Math.min(1, currentTime / duration)})`;
      }, { signal });

      // Vertical swipe on the stage (the drawer's own swipe-down-to-close is disabled while the stage is touched)
      let startY = null;
      this.stage.addEventListener('touchstart', (event) => {
        startY = event.touches[0].clientY;
        event.stopPropagation();
      }, { passive: true, signal });
      this.stage.addEventListener('touchmove', (event) => event.stopPropagation(), { passive: true, signal });
      this.stage.addEventListener('touchend', (event) => {
        event.stopPropagation();
        if (startY === null) return;
        const dy = event.changedTouches[0].clientY - startY;
        startY = null;
        if (Math.abs(dy) > 50) {
          this.suppressTap = true;
          setTimeout(() => (this.suppressTap = false), 350);
          this.go(dy < 0 ? 1 : -1);
        }
      }, { passive: true, signal });
    }

    open(index, opener) {
      if (!this.drawer || !this.player) return;
      this.playerOpen = true;
      this.pauseCards();
      this.show(index);
      this.drawer.open(opener);
    }

    show(index) {
      const count = this.cards.length;
      this.index = (index + count) % count;
      const card = this.cards[this.index];
      const src = card.dataset.src;
      this.captionEl.textContent = card.dataset.caption || '';
      this.bar.style.transform = 'scaleX(0)';
      this.pausedIcon.hidden = true;

      if (card.dataset.poster) this.player.poster = card.dataset.poster;
      else this.player.removeAttribute('poster');
      if (src) {
        this.player.src = src;
        this.player.muted = false;
        this.player.play().catch(() => {
          // Autoplay with sound can be refused; fall back to muted so the video still runs
          this.player.muted = true;
          this.player.play().catch(() => {});
        });
      } else {
        this.player.removeAttribute('src');
        this.player.load();
      }

      const template = card.parentElement.querySelector('template[data-reel-product]');
      this.productEl.innerHTML = '';
      this.productEl.hidden = !template;
      if (template) this.productEl.appendChild(template.content.cloneNode(true));
    }

    go(step) {
      if (!this.playerOpen) return;
      this.show(this.index + step);
    }

    toggle() {
      if (this.suppressTap) return;
      if (this.player.paused) {
        this.player.play().catch(() => {});
        this.pausedIcon.hidden = true;
      } else {
        this.player.pause();
        this.pausedIcon.hidden = false;
      }
    }

    onClose() {
      this.playerOpen = false;
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.load();
      // Let the cards resume if they are still in view
      this.cards.forEach((card) => {
        this.io?.unobserve(card);
        this.io?.observe(card);
      });
    }
  }

  customElements.define('otw-video-reels', OtwVideoReels);
})();
